import {
    afterNextRender,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25CheckboxModule } from "../../../modules/s25-checkbox/s25.checkbox.module";
import { S25ConflictsModule } from "../../../modules/s25-conflicts/s25.conflicts.module";
import { S25Datefilter } from "../../../modules/s25-dateformat/s25.datefilter.service";
import { S25ImageModule } from "../../../modules/s25-image/s25.image.module";
import { S25LoadingInlineStaticComponent } from "../../../modules/s25-loading/s25.loading.inline.static.component";
import { Layout } from "../../../modules/s25-object-search/s25.object.search.util";
import { S25SimpleCollapseModule } from "../../../modules/s25-simple-collapse/s25.simple.collapse.module";
import { Item } from "../../../pojo/Item";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { TelemetryService } from "../../../services/telemetry.service";
import { S25Util } from "../../../util/s25-util";
import { EventFormStore } from "../event.form.store";
import { EventForm } from "..";
import { S25AngJs } from "../../../util/core/s25.angjs";

const UNLIMITED_RESOURCE_QUANTITY = Math.pow(2, 31);

@Component({
    selector: "tr[s25-ng-event-form-object-occurrence-row]",
    standalone: true,
    imports: [
        FormsModule,
        S25CheckboxModule,
        S25ConflictsModule,
        S25ImageModule,
        S25LoadingInlineStaticComponent,
        S25PipesModule,
        S25SimpleCollapseModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "ngTableRow rose-object-occurrences-table--occurrence",
        "[class.has-layout-preview]": "showLayoutPreview()",
    },
    styleUrls: ["./event.form.object.occurrence.table.row.component.scss"],
    template: `
        <th class="show-on-mobile">{{ objOcc().occ.evStartDt | dateFormat: dateFormat() }}</th>
        <td class="ngTableCell rose-object-occurrences-table--date" tabindex="0" aria-label="Date" data-label="Date">
            <span class="ngBold">{{ objOcc().occ.evStartDt | dateFormat: dateFormat() }}</span>
            @if (objOcc().occ.spansMidnight) {
                <span class="ngBold"> - {{ objOcc().occ.evEndDt | dateFormat: dateFormat() }}</span>
            }
        </td>
        <td
            tabindex="0"
            aria-label="Time"
            class="ngTableCell"
            [class.ngBold]="timeDiffersFromModel()"
            data-label="Time"
        >
            <span
                >{{ objOcc().occ.evStartDt | dateFormat: timeFormat() }} -
                {{ objOcc().occ.evEndDt | dateFormat: timeFormat() }}</span
            >
        </td>
        <td class="ngTableCell" tabindex="0" aria-label="Conflicts" data-label="Conflicts">
            @if (conflictsLoading()) {
                <s25-ng-loading-inline-static [text]="'Loading Conflicts'" />
            }
            @if (exceedingQuantity(); as exceeding) {
                <s25-simple-collapse
                    class="quantity-collapse"
                    [expanded]="false"
                    [header]="{ text: 'Quantity Details', level: 2 }"
                >
                    <div class="cn-alert resource-quantity-warning">
                        <div class="ngBold c-margin-bottom--half cn-alert--warning">
                            Requesting {{ exceeding.reserved }} of {{ exceeding.max }} resources
                        </div>
                        <ul>
                            @for (exceedingOcc of exceeding.occs || []; track exceedingOcc.occ.uuid) {
                                <li>
                                    [{{ exceedingOcc.resourceQuantity }}] {{ exceedingOcc.startLabel }} -
                                    {{ exceedingOcc.endLabel }}
                                </li>
                            }
                        </ul>
                    </div>
                </s25-simple-collapse>
            }
            @if (conflicts().length) {
                <s25-simple-collapse
                    class="conflicts-collapse"
                    [class.conflicts-collapse--real]="hasRealConflict()"
                    [expanded]="false"
                    [header]="{ text: 'Conflict Details', level: 2 }"
                >
                    <s25-ng-conflicts class="conflicts-content" [model]="conflicts()" />
                </s25-simple-collapse>
            }
        </td>
        @if (!inline()) {
            <td class="ngTableCell c-text-align--center" data-label="Included">
                <s25-ng-checkbox
                    [value]="!!objOcc().isIncluded"
                    (valueChange)="onIncludedChange($event)"
                    [ariaLabel]="'Included'"
                    [noLabel]="true"
                    [labelId]="objOcc().occ.uuid + '-included'"
                />
            </td>
        }
        @if (isLocation() && canShare()) {
            <td class="ngTableCell c-text-align--center" data-label="Shared">
                <s25-ng-checkbox
                    [value]="!!objOcc().spaceShare"
                    (valueChange)="onShareChange($event)"
                    [ariaLabel]="'Shared'"
                    [noLabel]="true"
                    [labelId]="objOcc().occ.uuid + '-shared'"
                    [disabled]="alwaysShared()"
                />
            </td>
        }
        @if (isLocation() && hasLayouts()) {
            <td class="ngTableCell" data-label="Layout" [class.layout-preview]="showLayoutPreview()">
                <select
                    class="cn-form__control"
                    aria-label="Layout"
                    [ngModel]="objOcc().layout"
                    [compareWith]="isSameLayout"
                    (ngModelChange)="onLayoutSelect($event)"
                >
                    @for (layout of layouts(); track layout.layout_id) {
                        <option [ngValue]="layout">{{ layoutLabel(layout) }}</option>
                    }
                </select>
                @if (showLayoutPreview() && layoutImageId()) {
                    <s25-ng-image-preview [imageId]="layoutImageId()!" [buttonText]="'Preview'" />
                }
            </td>
        }
        @if (isLocation()) {
            <td class="ngTableCell" data-label="Instructions">
                <textarea
                    rows="1"
                    class="cn-form__control"
                    aria-label="Instructions"
                    [ngModel]="objOcc().spaceInstructions"
                    (ngModelChange)="onSpaceInstructionsChange($event)"
                ></textarea>
            </td>
        }
        @if (isResource()) {
            <td class="ngTableCell" data-label="Instructions">
                <textarea
                    rows="1"
                    class="cn-form__control"
                    aria-label="Instructions"
                    [ngModel]="objOcc().resourceInstructions"
                    (ngModelChange)="onResourceInstructionsChange($event)"
                ></textarea>
            </td>
        }
        @if (isLocation()) {
            <td class="ngTableCell" data-label="Attendance">
                <div class="numeric-wrapper">
                    <input
                        type="number"
                        class="c-numeric-input"
                        min="0"
                        max="65536"
                        aria-label="Attendance"
                        [ngModel]="objOcc().spaceAttendance"
                        (ngModelChange)="onAttendanceInput($event)"
                    />
                </div>
            </td>
        }
        @if (isResource()) {
            <td class="ngTableCell" data-label="Quantity">
                <div class="numeric-wrapper">
                    <input
                        type="text"
                        inputmode="numeric"
                        class="c-input c-numeric-input"
                        aria-label="Quantity"
                        [ngModel]="objOcc().resourceQuantity"
                        (ngModelChange)="onQuantityInput($event)"
                    />
                </div>
            </td>
            <td class="ngTableCell" aria-label="Availability" data-label="Availability">
                {{ availabilityLabel() }}
            </td>
        }
    `,
})
export class EventFormObjectOccurrenceTableRowComponent {
    readonly objOcc = input.required<EventForm.ObjectOccurrence>();
    readonly inline = input(false, { transform: (value: unknown) => S25Util.toBool(value) });
    readonly canShare = input(false);
    readonly layouts = input<Layout[]>([]);
    readonly alwaysShared = input(false);
    readonly dateFormat = input("");
    readonly timeFormat = input("");
    readonly dateTimeFormat = input("");

    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly eventForm = inject(EventFormStore);
    readonly isSameLayout = Layout.isSame;
    readonly layoutLabel = Layout.label;
    readonly conflictsLoading = signal(false);
    readonly showLayoutPreview = signal(false);
    readonly checkedView = signal<{
        isReal: boolean;
        conflicts: EventForm.ObjectOccurrenceConflict[];
        exceedingQuantity: EventForm.ObjectOccurrenceExceedingQuantity | false;
    } | null>(null);

    readonly conflicts = computed(() => this.checkedView()?.conflicts ?? []);
    readonly hasRealConflict = computed(() => !!this.checkedView()?.isReal);
    readonly exceedingQuantity = computed(() => this.checkedView()?.exceedingQuantity || null);

    constructor() {
        // React to external modifications of resourceQuantity
        effect(() => {
            const occurrence = this.objOcc();
            if (occurrence.item.itemTypeId !== Item.Ids.Resource) return;
            S25AngJs.onPropertyChange(occurrence, "resourceQuantity", () => this.onQuantityChange());
        });
        afterNextRender(() => {
            const current = this.objOcc();
            if (current.item.itemTypeId === Item.Ids.Resource) {
                current.prevResourceQuantity = current.resourceQuantity;
            } else {
                this.updateLayoutPreviewData();
            }
            this.updateHasConflicts();
        });
    }

    isLocation(): boolean {
        return this.objOcc().item.itemTypeId === Item.Ids.Location;
    }

    isResource(): boolean {
        return this.objOcc().item.itemTypeId === Item.Ids.Resource;
    }

    hasLayouts(): boolean {
        return this.layouts().length > 0;
    }

    maxQuantity(): number {
        return this.objOcc().maxQuantity ?? 65536;
    }

    isQuantityUnlimited(): boolean {
        return this.maxQuantity() === UNLIMITED_RESOURCE_QUANTITY;
    }

    layoutImageId(): number | null {
        const layout = this.objOcc().layout;
        return layout?.layout_photo_id || layout?.layout_diagram_id || null;
    }

    timeDiffersFromModel(): boolean {
        const objOcc = this.objOcc();
        const timeModel = this.eventForm.model().timeModel;
        const format = this.timeFormat();
        if (!format || !timeModel) return false;
        const occTimes =
            S25Datefilter.transform(objOcc.occ.evStartDt, format) + S25Datefilter.transform(objOcc.occ.evEndDt, format);
        const modelTimes =
            (S25Datefilter.transform(timeModel.evStartDt, format) ?? "") +
            (S25Datefilter.transform(timeModel.evEndDt, format) ?? "");
        return occTimes !== modelTimes;
    }

    availabilityLabel(): string {
        const objOcc = this.objOcc();
        const state = objOcc.occ.state;
        if (state.itemId !== 1) return state.itemName ?? "";
        if (this.isQuantityUnlimited()) return "Unlimited";
        return `${objOcc.maxQuantityDisplay}/${objOcc.item.quantity}`;
    }

    applyInclude(isIncluded: boolean): void {
        this.objOcc().isIncluded = S25Util.toBool(isIncluded);
        this.onIncludedChange();
    }

    applyShare(spaceShare: boolean): void {
        this.objOcc().spaceShare = S25Util.toBool(spaceShare);
        this.onShareChange();
    }

    applyInstructions(instructions: string): void {
        const objOcc = this.objOcc();
        if (objOcc.item.itemTypeId === Item.Ids.Location) {
            objOcc.spaceInstructions = instructions;
        } else if (objOcc.item.itemTypeId === Item.Ids.Resource) {
            objOcc.resourceInstructions = instructions;
        }
        this.refreshView();
    }

    applyAttendance(spaceAttendance: number): void {
        const objOcc = this.objOcc();
        objOcc.spaceAttendance = spaceAttendance;
        this.clampNonNegative(objOcc, "spaceAttendance");
        this.refreshView();
    }

    applyQuantity(resourceQuantity: number): void {
        this.objOcc().resourceQuantity = resourceQuantity;
        this.onQuantityChange();
    }

    applyLayout(layoutId: number): void {
        const layout = this.layouts().find((itemLayout) => itemLayout.layout_id === layoutId);
        if (layout) this.onLayoutSelect(layout, true);
    }

    refreshExceedingQuantity(): void {
        this.refreshAvailability(false);
    }

    onIncludedChange(isIncluded?: boolean): void {
        const objOcc = this.objOcc();
        if (isIncluded !== undefined) objOcc.isIncluded = isIncluded;
        if (objOcc.isIncluded && objOcc.resourceQuantity === 0) {
            objOcc.resourceQuantity = Math.min(1, objOcc.maxQuantity ?? 1);
        }
        this.refreshAvailability();
        this.refreshView();
    }

    onShareChange(spaceShare?: boolean): void {
        const objOcc = this.objOcc();
        if (spaceShare !== undefined) objOcc.spaceShare = spaceShare;
        this.refreshAvailability();
        this.refreshView();
    }

    onSpaceInstructionsChange(instructions: string): void {
        this.objOcc().spaceInstructions = instructions;
        this.refreshView();
    }

    onResourceInstructionsChange(instructions: string): void {
        this.objOcc().resourceInstructions = instructions;
        this.refreshView();
    }

    onAttendanceInput(value: number | string): void {
        const objOcc = this.objOcc();
        objOcc.spaceAttendance = value;
        this.clampNonNegative(objOcc, "spaceAttendance");
        this.refreshView();
    }

    onQuantityInput(value: number | string): void {
        const objOcc = this.objOcc();
        objOcc.resourceQuantity = value;
        this.clampResourceQuantity(objOcc);
        this.onQuantityChange();
    }

    onLayoutSelect(layout: Layout, fromChangingAllRows = false): void {
        const objOcc = this.objOcc();
        const previousLayout = objOcc.layout;
        if (previousLayout && !Layout.isSame(previousLayout, layout)) {
            this.eventForm.model().undoLayoutAdditionalTime?.(previousLayout);
        }
        objOcc.layout = layout;
        this.eventForm.model().updateSetupTakedownFromLayout?.(layout);
        this.eventForm.model().runRules?.();
        this.layoutChange(fromChangingAllRows);
    }

    private onQuantityChange(): void {
        const objOcc = this.objOcc();
        this.clampResourceQuantity(objOcc);
        if (objOcc.resourceQuantity === objOcc.prevResourceQuantity) return;
        objOcc.prevResourceQuantity = objOcc.resourceQuantity;
        objOcc.isIncluded = objOcc.resourceQuantity !== 0;
        this.refreshAvailability();
        this.refreshView();
        const formModel = this.eventForm.model();
        void formModel.refreshObjOccPending?.(objOcc.item, [objOcc.occ]).then(
            (success) => {
                if (!success) {
                    formModel.removeObject?.(objOcc.item);
                    alert(
                        `There was a problem updating the quantity of "${objOcc.item.itemName || "this resource"}". Refresh your resource search for conflict details.`,
                    );
                }
            },
            (caughtError) => {
                formModel.removeObject?.(objOcc.item);
                S25Util.showError(
                    caughtError,
                    "There was an error while attempting to hold the reservation for you. Please refresh and try again.",
                );
            },
        );
    }

    private layoutChange(fromChangingAllRows: boolean): void {
        if (!fromChangingAllRows) TelemetryService.send("EventForm", "SelectLayout");
        const objOcc = this.objOcc();
        objOcc.spaceInstructions = S25Util.coalesce(
            objOcc.layout?.layout_instruction,
            objOcc.item.defaultInstructions,
            objOcc.spaceInstructions,
        );
        this.updateLayoutPreviewData();
        this.eventForm.model().$broadcast?.(EventForm.BroadcastEvent.ObjectDetailsRefreshOccBasedData, objOcc.item);
        this.refreshView();
    }

    private updateLayoutPreviewData(): void {
        const objOcc = this.objOcc();
        this.showLayoutPreview.set(false);
        objOcc.showLayoutPreview = false;
        objOcc.layoutImageId = this.layoutImageId();
        queueMicrotask(() => {
            const showPreview = !!objOcc.layoutImageId;
            objOcc.showLayoutPreview = showPreview;
            this.showLayoutPreview.set(showPreview);
            this.refreshView();
        });
    }

    private refreshAvailability(showLoading = true): void {
        const objOcc = this.objOcc();
        if (showLoading) this.conflictsLoading.set(true);
        // Chain off the model's own promise. It is an AngularJS $q promise, and re-wrapping it in a
        // native promise adds a hop that does not settle within the digest that resolves it.
        const refresh = this.eventForm.model().refreshObjOccAvailability?.(objOcc.item, [objOcc.occ]);
        if (!refresh) {
            this.updateHasConflicts();
            return;
        }
        // The conflict repo is written as part of the same turn that resolves this promise, so the
        // results are only readable afterwards. The AngularJS directive used $timeout for this.
        void refresh.then(() => queueMicrotask(() => this.updateHasConflicts()));
    }

    private updateHasConflicts(): void {
        const objOcc = this.objOcc();
        const exceedingQuantity = objOcc.item.checkedObj?.exceedingQuantity?.find((quantity) =>
            quantity.occIds.has(objOcc.occ.uuid),
        );
        for (const exceedingOcc of exceedingQuantity?.occs || []) {
            const occStart = exceedingOcc.occ.evStartDt;
            const occEnd = exceedingOcc.occ.evEndDt;
            const startDate = S25Datefilter.transform(occStart, this.dateFormat());
            const startDateTime = S25Datefilter.transform(occStart, this.dateTimeFormat());
            const endDate = S25Datefilter.transform(occEnd, this.dateFormat());
            const endTime = S25Datefilter.transform(occEnd, this.timeFormat());
            exceedingOcc.startLabel = startDateTime;
            exceedingOcc.endLabel = startDate === endDate ? endTime : `${endDate} ${endTime}`;
        }
        const conflicts =
            (objOcc.isIncluded &&
                (objOcc.item.checkedObj?.conflicts ?? []).filter((conflict) => conflict.occUUID === objOcc.occ.uuid)) ||
            [];
        this.checkedView.set({
            isReal: conflicts.some((conflict) => !!conflict.isReal),
            conflicts,
            exceedingQuantity: (objOcc.isIncluded && exceedingQuantity) || false,
        });
        this.conflictsLoading.set(false);
        this.refreshView();
    }

    private clampNonNegative(objOcc: EventForm.ObjectOccurrence, prop: "spaceAttendance" | "resourceQuantity"): void {
        const value = objOcc[prop];
        if (typeof value === "number" && value < 0) objOcc[prop] = 0;
    }

    /** Matches legacy ui-numeric: clamp to min/max on change instead of surfacing conflicts. */
    private clampResourceQuantity(objOcc: EventForm.ObjectOccurrence): void {
        const parsed = S25Util.parseInt(objOcc.resourceQuantity);
        if (!Number.isFinite(parsed)) return;
        const clamped = this.isQuantityUnlimited() ? Math.max(0, parsed) : S25Util.clamp(parsed, 0, this.maxQuantity());
        if (objOcc.resourceQuantity !== clamped) {
            objOcc.resourceQuantity = clamped;
        }
    }

    /**
     * objOcc is the shared form object, so any write announced to this row also bumps the store.
     */
    private refreshView(): void {
        this.eventForm.bump();
        this.changeDetector.markForCheck();
    }
}
